
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './FeedbackDrawer.css';
import { submitFeedback } from './feedbackApi';
import config from './config';

// Helper to get items from order object
function getOrderItems(order) {
  if (order?.items && Array.isArray(order.items) && order.items.length > 0) return order.items;
  if (order?.OrderItemsJson) {
    try {
      const parsed = JSON.parse(order.OrderItemsJson);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
  }
  return [];
}

/**
 * FeedbackDrawer component
 * Props:
 *   open: boolean - controls visibility
 *   order: object - order details to display
 *   onClose: function - called to close the drawer
 *   onSubmit: function - called with { rating, comment, orderId } on submit
 */
const FeedbackDrawer = ({ open, order, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!open || !order) return null;

  const items = getOrderItems(order);

  const handleStarClick = (star) => setRating(star);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) return;
    setLoading(true);
    setError(null);
    try {
      // Get userId from localStorage
      const user = (() => {
        try {
          return JSON.parse(localStorage.getItem('user'));
        } catch {
          return null;
        }
      })();
      const userId = user?.Id;
      const customerId = order.CustomerId || order.customerId || userId;
      const orderId = order.OrderId || order.orderId || order.Id;
      console.log('[FeedbackDrawer] Submitting feedback:', { rating, comment, orderId, userId, customerId });
      await submitFeedback({ rating, comment, orderId, userId });

      // Update order status to Delivered after feedback
      const updatePayload = { OrderId: orderId, NewStatus: 'Delivered', CustomerId: customerId };
      console.log('[FeedbackDrawer] Updating order status:', updatePayload);
      const response = await fetch(`${config.API_BASE_URL}/Orders/UpdateStatus`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload)
      });
      const responseData = await response.json().catch(() => ({}));
      console.log('[FeedbackDrawer] UpdateStatus response:', response.status, responseData);

      setSuccess(true);
      setRating(0);
      setComment('');
      if (onSubmit) onSubmit({ rating, comment, orderId });
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('[FeedbackDrawer] Error submitting feedback or updating order:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feedback-drawer-backdrop" onClick={onClose}>
      <div className="feedback-drawer" onClick={e => e.stopPropagation()}>
        <h3>Rate Your Order</h3>
        <div className="order-list">
          <div><b>Order #{order.OrderId || order.orderId || order.Id}</b></div>
          <ul>
            {items.map((item, idx) => (
              <li key={idx}>
                {(item.ProductName || item.productName)} x{item.Quantity || item.quantity}
              </li>
            ))}
          </ul>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="star-rating">
            {[1,2,3,4,5].map(star => (
              <span
                key={star}
                className={star <= rating ? 'star selected' : 'star'}
                onClick={() => handleStarClick(star)}
                role="button"
                aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
              >★</span>
            ))}
          </div>
          <textarea
            placeholder="Leave a comment (optional)"
            value={comment}
            onChange={e => setComment(e.target.value)}
            rows={3}
            disabled={loading}
          />
          <button type="submit" disabled={rating === 0 || loading}>Submit Feedback</button>
        </form>
        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        {success && <div style={{ color: 'green', marginTop: 8 }}>Thank you for your feedback!</div>}
        <button className="close-btn" onClick={onClose} disabled={loading}>Close</button>
      </div>
    </div>
  );
};

FeedbackDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default FeedbackDrawer;
